import React from 'react'
import { MarketHead } from './MarketHead'
import { MarketBlogCards, MarketBlogTopics } from './MarketBlogCards'
import { absoluteUrl, breadcrumbNode, graph } from '../../utils/market-schema'
import { getBlogCategory, getMarket, marketBlogsPath } from '../../utils/markets'

// The UAE / Malaysia blog pages, laid out like Singapore's:
//  - MarketBlogsPage, the blog home (/ae/blogs) - pages/sg/blogs/index.js:
//    topic bar, About, the latest-blogs card grid, topic bar again;
//  - MarketBlogCategoryPage, one category (/ae/blogs/ac-guides) -
//    pages/sg/blogs/hvac-services/index.js: heading and card grid, then the
//    Topics bar.
// Cards come from lib/market-blogs.js - WordPress posts from the market's
// `blogWpCategories` first, then the in-repo guides.

// A listing, so CollectionPage + ItemList (not Article), in display order.
const listingJsonLd = ({ market, path, title, description, breadcrumbLabel, blogs }) => {
    const url = absoluteUrl(path)
    return graph(
        {
            "@type": "CollectionPage",
            "@id": `${url}/#webpage`,
            url,
            name: title,
            description,
            inLanguage: market.locale,
            breadcrumb: { "@id": `${url}/#breadcrumblist` },
            mainEntity: {
                "@type": "ItemList",
                numberOfItems: blogs.length,
                itemListElement: blogs.map((card, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    url: absoluteUrl(card.href),
                    name: card.title,
                })),
            },
        },
        breadcrumbNode(path, breadcrumbLabel),
    )
}

export const MarketBlogsPage = ({ countryCd, blogs = [], copy }) => {
    const market = getMarket(countryCd)
    const path = marketBlogsPath(market.countryCd)
    const jsonLd = listingJsonLd({ market, path, title: copy.title, description: copy.description, breadcrumbLabel: 'Blogs', blogs })

    return (
        <>
            <MarketHead market={market} path={path} title={copy.title} description={copy.description} jsonLd={jsonLd} />
            <MarketBlogTopics market={market} />
            <div className='max-w-5xl mx-auto py-10'>
                <div className='space-y-8 px-2'>
                    <div className='space-y-5'>
                        <div className='font-bold text-xl'>
                            About
                        </div>
                        <p className='text-gray-500'>
                            {copy.about}
                        </p>
                    </div>
                    <div className='space-y-5 pt-5'>
                        <h1 className='font-bold text-xl'>
                            {copy.h1}
                        </h1>
                        <MarketBlogCards cards={blogs} />
                    </div>
                </div>
            </div>
            <MarketBlogTopics market={market} />
        </>
    )
}

export const MarketBlogCategoryPage = ({ countryCd, vertical, blogs = [], copy }) => {
    const market = getMarket(countryCd)
    const category = getBlogCategory(market.countryCd, vertical)
    const text = copy.categories[vertical]
    const jsonLd = listingJsonLd({ market, path: category.path, title: text.title, description: text.description, breadcrumbLabel: category.label, blogs })

    return (
        <>
            <MarketHead market={market} path={category.path} title={text.title} description={text.description} jsonLd={jsonLd} />
            <div className='max-w-5xl mx-auto py-10'>
                <div className='space-y-8 px-2'>
                    <div className='space-y-5 pt-5'>
                        <h1 className='font-bold text-2xl md:text-3xl text-center'>
                            {text.h1}
                        </h1>
                        <MarketBlogCards cards={blogs} />
                    </div>
                </div>
            </div>
            <h2 className='bg-gray-800 text-white py-4 p-2 text-center text-2xl'>
                Topics
            </h2>
            <MarketBlogTopics market={market} />
        </>
    )
}
